/**
 * French tax rates indexed by fiscal year. Sourced from BOFiP +
 * Article 200 A CGI + Article 150 VH bis CGI.
 *
 * Stable since the PFU ("flat tax") came into effect on 1 January
 * 2018 — IR 12.8 % + prélèvements sociaux 17.2 % = 30 % global, with
 * a 305 € total-sales exemption threshold introduced for crypto by
 * Art. 150 VH bis when it was created in 2019.
 *
 * If a future PLF (loi de finances) changes any rate, only this file
 * needs an update — the calculator reads via `getFrenchTaxRatesForYear`.
 *
 * Created in Sprint V2.E4 (cf. `docs/v2/TAX_FR_AUDIT.md` §F12).
 */

export interface FrenchTaxRates {
  /** PFU 30 % (Art. 200 A CGI) — flat tax on capital gains + RCM. */
  flatTaxRate: number;
  /** Part IR du PFU (12.8 %). */
  incomeTaxRate: number;
  /** Prélèvements sociaux (17.2 % depuis 2018). */
  socialContributionsRate: number;
  /** Seuil de cessions totales en dessous duquel l'exonération
   *  s'applique (Art. 150 VH bis B 1°). */
  exemptionThreshold: number;
}

/**
 * Year-keyed table. Rates have been stable since 2018 — every year
 * inherits the same values until a législature change. We list each
 * year explicitly (rather than computing from a default) so future
 * divergence is a one-line change in the right cell.
 */
export const FRENCH_TAX_RATES: Record<number, FrenchTaxRates> = {
  2018: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2019: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2020: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2021: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2022: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2023: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2024: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2025: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
  2026: { flatTaxRate: 0.30, incomeTaxRate: 0.128, socialContributionsRate: 0.172, exemptionThreshold: 305 },
};

/** Default = latest year, used as fallback for missing years. */
export const DEFAULT_FRENCH_TAX_RATES: FrenchTaxRates = FRENCH_TAX_RATES[2026];

/**
 * Returns the rates for a given fiscal year. Falls back to the
 * default (latest year in the table) when the year is missing —
 * forgiving API consistent with the calculator's overall philosophy
 * (V2.E series convention).
 */
export function getFrenchTaxRatesForYear(year: number): FrenchTaxRates {
  return FRENCH_TAX_RATES[year] ?? DEFAULT_FRENCH_TAX_RATES;
}
